<template>
    <header-index />
    
    <!-- 面包屑导航 -->
    <div class="px-4 h-[60px] leading-[60px]" style="background-color: rgb(245, 245, 245);">
        <div class="flex mx-auto items-center text-sm mb-6 leading-[60px] container max-auto">
            <nuxt-link to="/" class="text-gray-500 hover:text-green-600">
                <span class="icon-home mr-1"></span> Home
            </nuxt-link>
            <span class="mx-2 text-gray-500">></span>
            <nuxt-link to="/packaging" class="text-gray-500 hover:text-green-600">Packaging</nuxt-link>
            <span class="mx-2 text-gray-500">></span>
            <span class="text-gray-700">Custom packaging</span>
        </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="container mx-auto py-12 px-4">
        <div class="max-w-4xl mx-auto">
            <!-- 标题部分 -->
            <div class="text-center mb-8">
                <h1 class="text-4xl font-weight-600 text-gray-900 mb-3">Custom Packaging</h1>
                <p class="text-lg text-gray-600">Find the Best Custom Packaging Solution for Your Brand</p>
            </div>

            <!-- 表单部分 -->
            <div class="bg-white p-8 shadow-lg rounded-lg">
                <form @submit.prevent="submitForm" class="space-y-5">
                    <!-- 产品 + 行业 + 联系人（一行三列） -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">Product*</label>
                            <select 
                                v-model="formData.product" 
                                class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                                :class="{ 'text-gray-400': !formData.product, 'text-black': formData.product }" 
                                required>
                                <option value="" disabled>Select a product</option>
                                <option value="boxes">Custom Boxes</option>
                                <option value="bags">Custom Bags</option>
                                <option value="labels">Custom Labels</option>
                                <option value="jars">Custom Jars</option>
                                <option value="tubes">Custom Tubes</option>
                                <option value="tins">Custom Tins</option>
                                <option value="other">Other Products</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">Industry*</label>
                            <select 
                                v-model="formData.industry" 
                                class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                                :class="{ 'text-gray-400': !formData.industry, 'text-black': formData.industry }" 
                                required>
                                <option value="" disabled>Select a product</option>
                                <option value="cannabis">Cannabis</option>
                                <option value="food-beverage">Food & Beverage</option>
                                <option value="cosmetics">Cosmetics & Beauty</option>
                                <option value="retail">Retail</option>
                                <option value="electronics">Electronics</option>
                                <option value="pharmaceutical">Pharmaceutical</option>
                                <option value="other">Other Industries</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">Contact Name*</label>
                            <input 
                                type="text" 
                                v-model="formData.contactName" 
                                class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500" 
                                required>
                        </div>
                    </div>

                    <!-- 邮箱 + 公司名（一行两列） -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">Email Address*</label>
                            <input
                                type="email"
                                v-model="formData.email"
                                class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                                required
                                :class="{'border-red-500': emailError}"
                                @blur="validateEmail">
                            <p v-if="emailError" class="text-red-500 text-sm mt-1">Please enter a valid email address.</p>
                        </div>

                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">Company Name*</label>
                            <input 
                                type="text" 
                                v-model="formData.companyName" 
                                class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                                required>
                        </div>
                    </div>

                    <!-- 电话 + 邮编（一行两列） -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">Phone Number</label>
                            <input
                                type="tel"
                                v-model="formData.phoneNumber"
                                class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                                :class="{'border-red-500': phoneError}"
                                @blur="validatePhone"
                               >
                            <p v-if="phoneError" class="text-red-500 text-sm mt-1">Please enter a valid phone number.</p>
                        </div>

                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">Zip Code</label>
                            <input
                                type="text"
                                v-model="formData.zipCode"
                                class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                                :class="{'border-red-500': zipCodeError}"
                                @blur="validateZipCode"
                                >
                            <p v-if="zipCodeError" class="text-red-500 text-sm mt-1">
                                Please enter a valid zip code.
                                <span class=" mt-1">Format: 12345 or 12345-6789</span>
                            </p>
                        </div>
                    </div>

                    <!-- 项目描述（一行两列：上传 + 描述） -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">File Upload*</label>
                            <div class="border border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:bg-gray-50 file-upload-container" @click="triggerFileInput">
                                <div class="flex items-center justify-center mb-1">
                                    <span class="text-blue-500">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                        </svg>
                                    </span>
                                </div>
                                <p class="text-xs text-gray-500">Support design files in AI/PDF format, or screenshots</p>
                                <input type="file" ref="fileInput" class="hidden" @change="handleFileChange" multiple>
                                <div v-if="formData.files.length > 0" class="mt-2 text-left">
                                    <p v-for="(file, index) in formData.files" :key="index" class="text-xs text-gray-700 py-0.5">
                                        {{ file.name }} ({{ formatFileSize(file.size) }})
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="block text-sm font-weight-400 mb-1">Please describe your project</label>
                            <textarea 
                                v-model="formData.message" 
                                class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 placeholder:text-gray-400"
                                rows="4"
                                placeholder="Describe your box style, dimensions, printing needs, and other requirements."
                                ></textarea>
                        </div>
                    </div>

                    <!-- 按钮组 -->
                    <div class="flex justify-center space-x-4 pt-4">
                        <button 
                            type="submit" 
                            :disabled="submitting"
                            :class="submitting
                                ? 'bg-green-400 cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700'"
                            class="text-white py-3 px-8 rounded transition duration-200 font-weight-400 disabled:opacity-70">
                            {{ submitting ? 'Waiting...' : 'Submit' }}
                        </button>
                        <button 
                            type="button" 
                            @click="resetForm" 
                            :disabled="submitting"
                            class="bg-gray-400 hover:bg-gray-500 text-white py-3 px-8 rounded transition duration-200 font-weight-400 disabled:opacity-50 disabled:cursor-not-allowed">
                            Reset
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <!-- Toast 消息 -->
    <toast-message ref="toastMessageRef" />

    <footer-index />
</template>

<script setup lang="ts" name="SinglePageCustomPackaging">
    import type { SinglePage, Response } from '~/types';
    import headerIndex from '../../components/header/header-index.vue'
    import footerIndex from '../../components/footer/footer-index.vue'
    import toastMessage from '../../components/common/toastMessage.vue'
    import { useHttp } from '~/utils/httpUtil'
    import cmsUtils from '~/utils/cmsUtils'
    import { ref } from 'vue'


    const cmsInfo = await cmsUtils.getCmsInfo();
    const props = defineProps<{
        singlePage: SinglePage
    }>()

    const fileInput = ref<HTMLInputElement | null>(null)
    // 提交中状态：防止重复提交，按钮置灰 + 文案变为 Waiting...
    const submitting = ref(false)
    // Toast 状态（复用 toastMessage 组件）
    const toastMessageRef = ref<InstanceType<typeof toastMessage> | null>(null)
    // 验证状态
    const phoneError = ref(false)
    const emailError = ref(false)
    const zipCodeError = ref(false)

    // Token 相关参数
    let token = ''
    let timestamp = '';
    let randomStr = ''

    const formData = ref({
        product: '',
        industry: '',
        files: [] as File[],
        message: '',
        contactName: '',
        email: '',
        phoneNumber: '',
        zipCode: '',
        companyName: ''
    })

    function resetForm() {
        formData.value = {
            product: '',
            industry: '',
            files: [],
            message: '',
            contactName: '',
            email: '',
            phoneNumber: '',
            zipCode: '',
            companyName: ''
        }
        emailError.value = false
        phoneError.value = false
        zipCodeError.value = false
    }

    function triggerFileInput() {
        if (fileInput.value) {
            fileInput.value.click()
        }
    }

    function handleFileChange(event: Event) {
        const input = event.target as HTMLInputElement
        if (input.files && input.files.length > 0) {
            const files = Array.from(input.files)
            formData.value.files = [...formData.value.files, ...files]
        }
    }

    function formatFileSize(size: number) {
        if (size < 1024) {
            return size + ' bytes'
        } else if (size < 1024 * 1024) {
            return (size / 1024).toFixed(2) + ' KB'
        } else {
            return (size / (1024 * 1024)).toFixed(2) + ' MB'
        }
    }

    function validateEmail() {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        emailError.value = !emailRegex.test(formData.value.email)
    }

    function validatePhone() {
        // 手机号只能包含数字、+、(、) 和空格（与后端校验规则一致）
        if (formData.value.phoneNumber.trim() === '') {
            phoneError.value = false
            return
        }
        const phoneRegex = /^[\+]?[1-9][\d\-]{0,15}$/
        phoneError.value = !phoneRegex.test(formData.value.phoneNumber.replace(/[\s\-\(\)]/g, ''))
    }

    function validateZipCode() {
        if (formData.value.zipCode.trim() === '') {
            zipCodeError.value = false
            return
        }
        const zipRegex = /^\d{5}(-\d{4})?$/
        zipCodeError.value = !zipRegex.test(formData.value.zipCode.trim())
    }

    async function submitForm() {
        // 防止重复提交
        if (submitting.value) return
        submitting.value = true

        // 验证所有字段
        validateEmail()
        validatePhone()
        validateZipCode()
        if (emailError.value || phoneError.value || zipCodeError.value) {
            toastMessageRef.value?.showFail('Please correct the errors in the form.')
            submitting.value = false
            return
        }

        // 创建FormData对象用于文件上传
        const formDataObj = new FormData()
        formDataObj.append('product', formData.value.product)
        formDataObj.append('industry', formData.value.industry)
        formDataObj.append('contactName', formData.value.contactName)
        formDataObj.append('email', formData.value.email)
        formDataObj.append('phoneNumber', formData.value.phoneNumber)
        formDataObj.append('zipCode', formData.value.zipCode)
        formDataObj.append('companyName', formData.value.companyName)
        formDataObj.append('message', formData.value.message)
        // 每次提交时实时生成 token，确保 timestamp/randomStr/token 三者自洽（避免 onMounted 缓存导致 token 失效）
        formDataObj.append('token', await generateToken(cmsInfo.website.id.toString()))
        formDataObj.append('websiteId', cmsInfo.website.id.toString())
        formDataObj.append('timestamp', timestamp)
        formDataObj.append('randomStr', randomStr)
        // 来源页面 URL（用于追溯留言提交位置）
        formDataObj.append('sourceUrl', typeof window !== 'undefined' ? window.location.href : '')

        // 添加文件
        formData.value.files.forEach((file) => {
            formDataObj.append('file', file)
        })

        try {
            const res = await useHttp("/nuxt/common/addUserConsultant", {
                method: 'POST',
                body: formDataObj,
                headers: {}
            }) as Response<any>

            if (res.code === 200) {
                toastMessageRef.value?.showSuccess('Thank you for your inquiry! We will reply to you as soon as possible!')
                resetForm()
            } else if (res.code === 401) {
                toastMessageRef.value?.showFail('Token is timeout, please refresh the page and try again.')
            } else {
                toastMessageRef.value?.showFail('Server error, please contact administrator!')
            }

        } catch (error: any) {
            console.error('Error submitting form:', error)
            toastMessageRef.value?.showFail(error?.msg || 'There was an error submitting your form. Please try again.')
        } finally {
            submitting.value = false
        }
    }

    // 获取时间戳
    let res = await useHttp("/nuxt/common/getTimestamp", {
        method: 'POST',
        body: {},
        headers: {}
    }) as Response<string>
    timestamp = res.data

    // 生成 Token（提交时实时调用，确保 timestamp/randomStr/token 自洽）
    async function generateToken(websiteId: string) {
        let randomArray = new Uint8Array(16);
        window.crypto.getRandomValues(randomArray);
        randomStr = Array.from(randomArray).map(b => b.toString(16).padStart(2, '0')).join('');
        const data = `${websiteId}:${timestamp}:${randomStr}`;

        // 使用 Web Crypto API 计算哈希
        const hash = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(data))
        const hashArray = Array.from(new Uint8Array(hash));
        token = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return token
    }
</script>

<style scoped lang='scss'>
    .icon-home {
        display: inline-block;
        width: 14px;
        height: 14px;
        background-size: contain;
        background-repeat: no-repeat;
        vertical-align: middle;
    }

    .file-upload-container {
        transition: all 0.3s ease;

        &:hover {
            border-color: #059669;
        }
    }

    /* 表单样式优化 */
    .form-group {
        label {
            color: #374151;
            font-weight: 400;
        }

        input, select, textarea {
            transition: all 0.2s ease;
            
            &:focus {
                border-color: #059669;
                box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
            }
        }

        select {
            appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
            background-position: right 0.5rem center;
            background-repeat: no-repeat;
            background-size: 1.5em 1.5em;
            padding-right: 2.5rem;
        }
    }

    /* 按钮悬停效果 */
    button {
        font-size: 16px;
        min-width: 120px;
        
        &:active {
            transform: scale(0.98);
        }
    }
</style>

